import { PanelRightCloseIcon, PanelRightOpenIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import type { ImportViolation } from '@/lib/api/generated/model';
import { Button } from '@/components/ui/button';
import { RequiredMark } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { SECTION_HEADING } from '@/shared/components/sectionHeading';

import { useDraft } from '../builder/useDraftContext';
import { findStep, setWorkflowField } from '../builder/draftOps';
import { FieldErrorProvider } from '../builder/FieldErrorProvider';
import { useFieldError } from '../builder/useFieldError';
import type { BuilderSelection } from '../builder/selection';
import { CommitField } from './CommitField';
import { ImagePicker } from './ImagePicker';
import { PhaseInspector } from './PhaseInspector';
import { ProblemList } from './ProblemList';
import { StepInspector } from './StepInspector';
import { TransitionInspector } from './TransitionInspector';

/**
 * The inspector: everything the selected element declares.
 *
 * **Docked, not modal** (FR-026). It is a flex sibling of the canvas with an
 * `aria-label` and deliberately no `role="dialog"` — the canvas stays live
 * behind it, so a user can select a different node without dismissing anything
 * first. A dialog would trap focus and make editing a graph a sequence of
 * open-edit-close cycles.
 *
 * With nothing selected it shows the workflow itself (FR-027), rather than an
 * empty panel telling the user to go and select something. The workflow's own
 * name and description are real fields that need somewhere to live, and this is
 * the place where "nothing in particular is selected" naturally means "the
 * whole thing".
 *
 * **It folds to a rail**, the way the palette on the other side of the canvas
 * does. Docked means the canvas is permanently narrower, which is the right
 * trade while someone is editing fields and the wrong one while they are
 * arranging nodes — and which of those they are doing is not something this
 * component can infer. Folding is how the user says which, and the choice is a
 * device preference rather than part of the address: it describes how much
 * canvas this person wants, not what they are looking at.
 *
 * Folded, it keeps its heading. The palette can collapse to icons that are
 * still draggable, but an inspector's content is fields — there is no smaller
 * version of a textarea — so what a rail can honestly offer is the name of what
 * is behind it and the way back.
 */
export function BuilderInspector({
  workspaceId,
  selection,
  onSelect,
  onSelectProblem,
  problems = [],
  fieldErrors = {},
  onResolveField,
  collapsed,
  onToggleCollapsed,
}: {
  /**
   * Needed to reach the workspace's tasks (phase-delete guard) and its LLM
   * profiles (the step editor's profile picker, 067 US2).
   */
  workspaceId: string;
  selection: BuilderSelection;
  onSelect: (selection: BuilderSelection) => void;
  /**
   * Selecting *because a problem said so*, which is not the same act as
   * selecting: it also marks the element and moves the canvas to it.
   */
  onSelectProblem: (selection: BuilderSelection) => void;
  /** Why the last save or publish was refused, if it was. */
  problems?: ImportViolation[];
  /**
   * The subset of that refusal about the selected element's own fields, keyed
   * by field name — already narrowed, so a body does not re-derive it.
   */
  fieldErrors?: Record<string, string>;
  /** Editing a field withdraws the refusal that named it. */
  onResolveField?: (field: string) => void;
  /** Folded to a rail, so the canvas gets the width back. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const title = titleFor(selection);

  return (
    <aside
      aria-label="Inspector"
      data-testid="builder-inspector"
      data-collapsed={collapsed}
      className={cn(
        // `overflow-x-hidden` is what makes the width transition watchable: the
        // fields inside keep their width, so they are revealed by the edge
        // moving rather than re-wrapping on every frame. Same reason the
        // palette's rail has it — see BuilderPage.
        'border-border bg-card flex shrink-0 flex-col overflow-x-hidden rounded-lg border transition-[width]',
        collapsed
          ? // Wide enough for a 28px control and its padding, and no wider.
            'w-11 gap-2 p-2'
          : // The same width as the read-only page's floating step panel, so a
            // prompt wraps identically whether you are reading a workflow or
            // editing one. It costs the canvas 4rem more than it used to, which
            // is what folding is for.
            'w-96 gap-4 overflow-y-auto p-4',
      )}
    >
      {/*
        The toggle is anchored to the outer edge in both states, so it does not
        jump the width of the panel as the panel folds — the mirror of what the
        palette does with its own toggle on the left.
      */}
      <div className="flex flex-none items-center gap-1.5">
        {!collapsed && (
          // `text-base`, matching the read-only page's panel heading — and
          // outranking the `text-sm` sub-headings inside this one, which the
          // panel title previously matched exactly. A title that is the same
          // size as the sections under it does not read as their title.
          //
          // It says the *kind*, where the read-only panel says the step's name.
          // That difference is not an oversight: every body below edits the
          // thing's name as its first field, so a heading echoing it would be a
          // second copy of an input, rewriting itself as the user types. The
          // read-only panel has no such field, which is why the name has to be
          // the heading there.
          <h2 className="text-foreground min-w-0 truncate text-base font-medium">
            {title}
          </h2>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleCollapsed}
          aria-label={
            collapsed ? 'Expand the inspector' : 'Collapse the inspector'
          }
          aria-expanded={!collapsed}
          // Named only while the thing it names is rendered: pointing at an id
          // that is not in the document describes nothing.
          aria-controls={collapsed ? undefined : 'builder-inspector-body'}
          // An *inset* ring, and the button's own outward one cancelled: folded,
          // this control fills the rail exactly, and the container clips
          // horizontally — so a ring drawn outside the box is a focus indicator
          // nobody can see.
          className="focus-visible:inset-ring-ring ml-auto shrink-0 opacity-50 hover:opacity-100 focus-visible:inset-ring-2 focus-visible:ring-0 focus-visible:outline-none"
        >
          {collapsed ? <PanelRightOpenIcon /> : <PanelRightCloseIcon />}
        </Button>
      </div>

      {collapsed ? (
        <>
          {/*
            A refusal unfolds the panel, so a folded one with problems
            outstanding means the user folded it afterwards — their choice, but
            not a reason to let the count disappear. The list itself needs the
            width; the fact that it exists does not (FR-008).
          */}
          {problems.length > 0 && (
            <span
              data-testid="inspector-problem-count"
              aria-label={`${String(problems.length)} ${
                problems.length === 1 ? 'problem' : 'problems'
              }`}
              className="border-destructive/40 bg-destructive/10 text-destructive flex h-5 shrink-0 items-center justify-center rounded-full border text-xs font-medium"
            >
              {problems.length}
            </span>
          )}
          {/*
            Turned on its side rather than abbreviated: the rail is 28px of
            usable width, which fits an icon but not a word, and an icon for
            "whatever is selected" would have to stand for four different
            things.

            Still the heading, not decoration. Folding costs a sighted user the
            fields; it should not additionally cost a screen-reader user the one
            word saying what folding put away.

            `self-center`: turned on its side the heading is one line tall and
            one line-height wide, and a stretched flex child sits that narrow
            box at the rail's left edge — a few pixels off the toggle's centre
            above it, which is exactly the kind of misalignment a rail this
            narrow cannot hide.
          */}
          {/* The shared section heading, turned on its side. It was a
              near-miss of it — `font-medium tracking-wide` against
              `font-semibold tracking-wider` — which reads as a mistake rather
              than as a different kind of heading. */}
          <h2
            className={cn(
              SECTION_HEADING,
              'min-h-0 self-center truncate [writing-mode:vertical-rl]',
            )}
          >
            {title}
          </h2>
        </>
      ) : (
        <div
          id="builder-inspector-body"
          className="flex min-h-0 flex-col gap-4"
        >
          {/*
            Above the selected element's fields, because it is the reason the
            user is looking at the panel at all — and because each entry
            navigates the selection, which changes what appears below it.
          */}
          <ProblemList
            violations={problems}
            onSelect={onSelectProblem}
            actionFor={(violation) => (
              <OpenDraftAction
                workspaceId={workspaceId}
                violation={violation}
              />
            )}
          />

          {/*
            Says *that* a marked field is required, not when — the tiers differ
            (a step's name is refused on any save, its prompt only on publish)
            and each field's own hint carries that. One legend claiming a single
            deadline would be wrong for half of them.

            Above the fields rather than under them. A step's panel runs past the
            fold — transitions are the last section — so a key placed at the end
            is a key nobody scrolls to, explaining marks they met screens
            earlier. Here it sits beside the first one it describes.

            Every body marks something — a phase's name is required too — so
            there is no selection this key does not describe.
          */}
          <p className="text-muted-foreground flex items-center gap-1 text-xs">
            <RequiredMark /> required
          </p>

          <FieldErrorProvider errors={fieldErrors} onResolve={onResolveField}>
            <Body
              workspaceId={workspaceId}
              selection={selection}
              onSelect={onSelect}
            />
          </FieldErrorProvider>
        </div>
      )}
    </aside>
  );
}

function titleFor(selection: BuilderSelection): string {
  if (!selection) return 'Workflow';
  switch (selection.kind) {
    case 'step':
      return 'Step';
    case 'transition':
      return 'Transition';
    case 'phase':
      return 'Phase';
  }
}

function Body({
  workspaceId,
  selection,
  onSelect,
}: {
  workspaceId: string;
  selection: BuilderSelection;
  onSelect: (selection: BuilderSelection) => void;
}) {
  if (!selection) return <WorkflowInspector workspaceId={workspaceId} />;

  switch (selection.kind) {
    case 'step':
      return (
        <StepInspector
          workspaceId={workspaceId}
          stepId={selection.id}
          onSelect={onSelect}
        />
      );
    case 'transition':
      return (
        <TransitionInspector
          stepId={selection.stepId}
          transitionKey={selection.key}
          onSelect={onSelect}
        />
      );
    case 'phase':
      return (
        <PhaseInspector
          workspaceId={workspaceId}
          phaseId={selection.id}
          onSelect={onSelect}
        />
      );
  }
}

/** The workflow's own fields — what "nothing selected" means (FR-027). */
function WorkflowInspector({ workspaceId }: { workspaceId: string }) {
  const { draft, edit } = useDraft();
  const { resolve: resolveName } = useFieldError('name');

  return (
    <div className="flex flex-col gap-5">
      <CommitField
        label="Name"
        value={draft.name}
        required
        field="name"
        onCommit={(name) => {
          edit(setWorkflowField('name', name));
          resolveName();
        }}
      />
      <CommitField
        label="Description"
        value={draft.description ?? ''}
        multiline
        rows={3}
        placeholder="What this workflow is for."
        onCommit={(description) =>
          edit(setWorkflowField('description', description || null))
        }
      />
      <ImagePicker
        scope="workflow"
        workspaceId={workspaceId}
        value={draft.image}
        onCommit={(image) => edit(setWorkflowField('image', image))}
      />
      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground w-20 shrink-0 text-xs">Key</dt>
          <dd className="text-foreground font-brand-mono min-w-0 truncate">
            {draft.loaded.key}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground w-20 shrink-0 text-xs">
            Status
          </dt>
          <dd className="text-foreground">
            {draft.status === 'draft' ? 'Draft' : 'Published'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * "Open draft" beside a publish refused for a draft link (spec 202 US3).
 *
 * The linking step's own target is opened, read from the draft being edited
 * rather than parsed out of the message: when the draft is further down, that
 * target is still the way to reach it.
 */
function OpenDraftAction({
  workspaceId,
  violation,
}: {
  workspaceId: string;
  violation: ImportViolation;
}) {
  const { draft } = useDraft();
  if (violation.category !== 'draft_link' || !violation.step_id) return null;
  const target = findStep(draft, violation.step_id)?.workflow;
  if (!target) return null;
  return (
    <Link
      to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
      params={{ workspaceId, workflowKey: target }}
      className="text-primary underline-offset-4 hover:underline"
    >
      Open draft
    </Link>
  );
}
